import React from 'react';
import { Check, X } from 'lucide-react';

export const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  { label: 'One special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export const isPasswordValid = (password: string) => PASSWORD_RULES.every(r => r.test(password));

export const getPasswordStrength = (password: string): number =>
  PASSWORD_RULES.filter(r => r.test(password)).length;

interface PasswordStrengthIndicatorProps {
  password: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  if (!password) return null;

  const strength = getPasswordStrength(password);
  const strengthLabel = strength <= 2 ? 'Weak' : strength <= 4 ? 'Fair' : 'Strong';
  const strengthColor = strength <= 2 ? 'bg-destructive' : strength <= 4 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-2">
      {/* Strength bar */}
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength ? strengthColor : 'bg-muted'}`}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Strength: {strengthLabel}</p>

      {/* Rules checklist */}
      <ul className="space-y-1">
        {PASSWORD_RULES.map((rule, i) => {
          const passed = rule.test(password);
          return (
            <li key={i} className={`flex items-center gap-1.5 text-xs ${passed ? 'text-green-600' : 'text-muted-foreground'}`}>
              {passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
              {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PasswordStrengthIndicator;
